import {
    GUNB_EXCERPT_LABEL,
    bdot10kImpactHelp,
    bdot10kLayerLabel,
    breakableHtml,
    countLabel,
    dateLong,
    expandAddressAbbreviations,
    formatDist,
    gunbExcerptQuote,
    parcelOwnershipHelp,
    renderInfoHelpButton,
    signalTargetLabel,
    topographicStoreysToShow,
    TOPOGRAPHIC_STOREYS_HELP,
    TOPOGRAPHIC_STOREYS_HELP_LABEL,
    TOPOGRAPHIC_STOREYS_LABEL
} from '../utils/formatters';
import { soundLevelLabel } from '../utils/decibels';
import { guideLine } from '../../shared/guide-link.js';
import { ownershipColor } from '../core/map-legend-definitions';
import { FOREST_LAND_USE_WARNING, LAND_USE_CATEGORIES, hasForestLandUse, landUseCategory, landUseCodeDetails, landUseReadable } from '../core/land-use-definitions';
import { educationFacilityCardHtml } from '../layers/EducationFacilitiesLayer';
import { bandLabel, indicatorShortLabel } from '../layers/NoiseContourLayer';
import { educationLayerRuntime } from '../core/education-layer-runtime';

export function noiseSwatchModifier(properties, isMonitoring) {
    var props = properties || {};
    if (isMonitoring) {
        var assessment = ['low', 'moderate', 'high', 'very_high'].indexOf(props.assessmentKey) !== -1
            ? props.assessmentKey.replace('_', '-')
            : 'unknown';
        return 'nm-noise-swatch--monitoring-' + assessment;
    }

    var rawMinimum = props.dbMin;
    var minimum = rawMinimum === null || rawMinimum === undefined || rawMinimum === ''
        ? NaN
        : Number(rawMinimum);
    if (!Number.isFinite(minimum)) return 'nm-noise-swatch--unknown';
    if (minimum < 55) return 'nm-noise-swatch--under-55';
    if (minimum < 60) return 'nm-noise-swatch--55';
    if (minimum < 65) return 'nm-noise-swatch--60';
    if (minimum < 70) return 'nm-noise-swatch--65';
    if (minimum < 75) return 'nm-noise-swatch--70';
    return 'nm-noise-swatch--75';
}

export function createTapBrowseManager(config) {
    var state = config.state;

    function renderOwnershipChip(properties) {
        var props = properties || {};
        var label = props.ownership_label || 'Brak danych';
        var group = props.grupa_rejestrowa != null ? ' · grupa ' + props.grupa_rejestrowa : '';
        var color = ownershipColor(props.grupa_rejestrowa);
        var help = parcelOwnershipHelp(props);
        return '<div class="nm-parcel-card__attribute" style="--nm-ownership-color:' + color + '">'
            + '<span class="nm-ownership-swatch" aria-hidden="true"></span>'
            + '<span class="nm-parcel-card__attribute-label">Własność</span> '
            + '<strong class="nm-parcel-card__attribute-value">' + config.esc(label + group) + '</strong>'
            + renderInfoHelpButton(help, 'Wyjaśnienie kategorii własności działki')
            + '</div>';
    }

    function renderLandUseChip(properties) {
        var codes = properties && properties.klasouzytki_egib;
        var category = landUseCategory(codes);
        var details = landUseCodeDetails(codes);
        var readable = landUseReadable(codes);
        var agricultural = details.some(function(item) { return item.category === LAND_USE_CATEGORIES.agricultural.label; });
        var forest = hasForestLandUse(details);
        // The chip names the land uses; the popup adds the group of each one, what the
        // land use means for building and where to confirm it.
        var help = details.length
            ? '<div class="info-popup-title">Sposób użytkowania gruntu zapisany w ewidencji gruntów i budynków</div>'
                + '<p><strong>Grupa użytków:</strong></p>'
                + '<ul>' + details.map(function(item) {
                    return '<li><strong>' + config.esc(item.label) + '</strong> — ' + config.esc(item.category) + '</li>';
                }).join('') + '</ul>'
                + (details.length > 1 ? '<p>Bez powierzchni poszczególnych części nie wskazujemy użytku dominującego.</p>' : '')
                + '<p><strong>Znaczenie:</strong> użytek mówi, jak grunt jest używany według ewidencji, i sam nie przesądza o zabudowie. Co wolno zbudować, ustala plan miejscowy albo decyzja o warunkach zabudowy.</p>'
                + (agricultural
                    ? '<p><strong>Uwaga:</strong> budowa na gruncie rolnym może wymagać wyłączenia go z produkcji rolniczej. Przy glebach lepszych klas potrzebna jest decyzja starosty i mogą dojść opłaty; klasę gleby podaje wypis z rejestru gruntów.</p>'
                    : '')
                + (forest ? '<p><strong>Uwaga:</strong> ' + config.esc(FOREST_LAND_USE_WARNING) + '</p>' : '')
                + '<p>' + config.esc(agricultural
                    ? guideLine('slownik#wylaczenie-gruntu-z-produkcji', 'wyłączenie gruntu z produkcji rolniczej')
                    : (forest ? guideLine('slownik#plan-miejscowy', 'plan miejscowy') : guideLine('slownik#wypis-z-rejestru-gruntow', 'wypis z rejestru gruntów'))) + '</p>'
            : '<div class="info-popup-title">Ewidencja gruntów i budynków nie podaje użytku tej działki</div>'
                + '<p><strong>Znaczenie:</strong> rodzaj użytku i klasę gleby podaje wypis z rejestru gruntów, który wydaje starostwo.</p>'
                + '<p>' + config.esc(guideLine('slownik#wypis-z-rejestru-gruntow', 'wypis z rejestru gruntów')) + '</p>';
        return '<div class="nm-parcel-card__attribute" style="--nm-land-use-color:' + category.color + '">'
            + '<span class="nm-land-use-swatch" aria-hidden="true"></span>'
            + '<span class="nm-parcel-card__attribute-label">Użytek</span> '
            + '<strong class="nm-parcel-card__attribute-value">' + config.esc(readable) + '</strong>'
            + renderInfoHelpButton(help, 'Wyjaśnienie użytków gruntowych działki')
            + '</div>';
    }

    // The register's storeys, else (none or 0) the BDOT10k value under a label naming that source.
    function renderBuildingStoreysChip(properties) {
        var topographic = topographicStoreysToShow(properties);
        if (topographic != null) {
            return '<span class="nm-chip"><b>' + TOPOGRAPHIC_STOREYS_LABEL + '</b> ' + config.esc(topographic)
                + renderInfoHelpButton(TOPOGRAPHIC_STOREYS_HELP, TOPOGRAPHIC_STOREYS_HELP_LABEL) + '</span>';
        }
        return properties.kondygnacje != null
            ? '<span class="nm-chip"><b>Kondygnacje</b> ' + config.esc(properties.kondygnacje) + '</span>'
            : '';
    }

    function clearTapBrowseState() {
        state.setTapBrowseHits(null);
        state.setTapBrowseLatlng(null);
    }

    function exitCombinedBrowse() {
        state.setCombinedBrowseTransactions(null);
        state.setCombinedBrowseGunb(null);
        state.setCombinedBrowseOtherHits(null);
        state.setCombinedBrowseLatlng(null);
        config.renderListPanel({ immediate: true });
    }

    function browseTapHitsInPanel(latlng, hits) {
        state.setOverlapBrowseFeatures(null);
        state.setOverlapBrowseLatlng(null);
        state.setGunbBrowseFeatures(null);
        state.setGunbBrowseLatlng(null);
        state.setCombinedBrowseTransactions(null);
        state.setCombinedBrowseGunb(null);
        state.setCombinedBrowseOtherHits(null);
        state.setCombinedBrowseLatlng(null);
        state.setTapBrowseHits(hits);
        state.setTapBrowseLatlng(latlng);
        config.renderListPanel();
        config.setTab('list', { snap: 'half' });
    }

    function exitTapBrowse() {
        clearTapBrowseState();
        config.renderListPanel({ immediate: true });
    }

    function renderTapHitCardHtml(hit) {
        if (!hit) {
            return '';
        }

        var selectedIds = config.getSelectedIds();

        if (hit.type === 'noise') {
            var noiseProps = hit.properties || {};
            var isMonitoring = hit.layerKey === 'noiseMonitoring';
            var indicator = String(noiseProps.indicator || (hit.layerKey === 'noiseLn' ? 'LN' : 'LDWN')).toUpperCase();
            var title = isMonitoring
                ? (noiseProps.aggregate ? 'Pomiary hałasu w tym obszarze' : 'Punkt monitoringu hałasu')
                : 'Strategiczna mapa hałasu';
            var subtitle = isMonitoring
                ? (noiseProps.pointName ? expandAddressAbbreviations(noiseProps.pointName) : (noiseProps.locality || 'Wynik pomiaru w wybranym miejscu'))
                : ({ road: 'Hałas drogowy', rail: 'Hałas kolejowy', air: 'Hałas lotniczy', agglomeration: 'Hałas na obszarze miejskim' }[noiseProps.noiseType] || 'Modelowany poziom hałasu');
            var measuredValue = noiseProps.value != null && noiseProps.value !== '' && Number.isFinite(Number(noiseProps.value));
            var value = isMonitoring
                ? (measuredValue ? soundLevelLabel(noiseProps.value, noiseProps.unit) : '')
                : (noiseProps.dbMin != null && noiseProps.dbMin !== '' ? bandLabel(noiseProps) : '');
            var swatchModifier = noiseSwatchModifier(noiseProps, isMonitoring);

            return '<button class="ms-card ms-card--grid nm-list-card" type="button" data-tap-hit-kind="noise" data-tap-hit-id="' + config.esc(String(hit.id)) + '">' +
                '<div class="nm-list-card-top">' +
                    '<span class="nm-pill"><span class="nm-noise-swatch ' + swatchModifier + '" aria-hidden="true"></span>Hałas</span>' +
                    '<span class="nm-chip">' + config.esc(isMonitoring ? 'Monitoring' : indicatorShortLabel(indicator)) + '</span>' +
                '</div>' +
                '<div class="nm-list-address">' + config.esc(title) + '</div>' +
                '<div class="nm-card-subline">' + config.esc(subtitle) + '</div>' +
                (value ? '<div class="nm-list-meta-row"><span class="nm-chip"><b>Poziom</b> ' + config.esc(value) + '</span></div>' : '') +
            '</button>';
        }

        if (hit.type === 'education') {
            return educationFacilityCardHtml(hit, educationLayerRuntime.getMetadata());
        }

        if (hit.type === 'gunb') {
            var gunbProps = hit.feature && hit.feature.properties ? hit.feature.properties : {};
            return '<button class="ms-card ms-card--grid nm-list-card nm-gunb-card' +
                (String(gunbProps.id) === String(selectedIds.selectedGunbId) ? ' is-selected' : '') +
                '" type="button" data-tap-hit-kind="gunb" data-tap-hit-id="' + config.esc(String(hit.id)) + '">' +
                '<div class="nm-list-card-top">' +
                    '<span class="nm-pill nm-pill--gunb">' + config.esc(gunbProps.rodzaj_inwestycji || 'Pozwolenie') + '</span>' +
                '</div>' +
                // The type of works in plain words and the safe excerpt of the register's description;
                // the investor only when the API names one (a company or an institution).
                (gunbProps.roboty ? '<div class="nm-list-address">' + config.esc(gunbProps.roboty) + '</div>' : '') +
                (gunbProps.opis ? '<div class="nm-card-subline">' + config.esc(GUNB_EXCERPT_LABEL + ': ' + gunbExcerptQuote(gunbProps.opis)) + '</div>' : '') +
                '<div class="nm-list-meta-row">' +
                    // The category by its name (the register's Roman numeral alone means nothing), in the
                    // row of chips, where a long name has the width of the card.
                    (gunbProps.kategoria_label ? '<span class="nm-chip">' + config.esc(gunbProps.kategoria_label) + '</span>' : '') +
                    (gunbProps.data_wplywu_wniosku ? '<span class="nm-chip"><b>Wniosek złożony</b> ' + config.esc(dateLong(gunbProps.data_wplywu_wniosku)) + '</span>' : '') +
                    (gunbProps.data_wydania_decyzji ? '<span class="nm-chip"><b>Decyzja wydana</b> ' + config.esc(dateLong(gunbProps.data_wydania_decyzji)) + '</span>' : '') +
                    (gunbProps.numer_decyzji ? '<span class="nm-chip"><b>Numer decyzji</b> ' + config.esc(gunbProps.numer_decyzji) + '</span>' : '') +
                    (gunbProps.inwestor ? '<span class="nm-chip"><b>Inwestor</b> ' + config.esc(gunbProps.inwestor) + '</span>' : '') +
                    (gunbProps.id_dzialki ? '<span class="nm-chip"><b>Działka</b> ' + config.esc(gunbProps.id_dzialki) + '</span>' : '') +
                '</div>' +
            '</button>';
        }

        if (hit.type === 'otoczenie') {
            var otoczenieFeature = hit.feature || {};
            var otoczenieProps = otoczenieFeature.properties || hit.properties || {};
            var otoczenieLabel = config.bdot10kLabel(otoczenieProps);
            var impactSummary = config.bdot10kImpactSummary(otoczenieProps);
            var balanceLabel = config.bdot10kImpactBalanceLabel(otoczenieProps);
            var impactClassificationLabel = config.bdot10kImpactClassificationLabel(otoczenieProps);
            var impactChipClass = otoczenieProps.negative ? 'nm-chip--danger' : (otoczenieProps.impact_classification === 'useful' ? 'nm-chip--success' : '');
            var impactHelp = bdot10kImpactHelp(otoczenieProps);
            var otoczenieLayerLabel = bdot10kLayerLabel(otoczenieProps);
            return '<article class="ms-card ms-card--grid nm-list-card nm-otoczenie-card" data-tap-hit-kind="otoczenie" data-tap-hit-id="' + config.esc(String(hit.id)) + '">' +
                '<button class="nm-list-card-select" type="button">' +
                '<div class="nm-list-card-top">' +
                    '<span class="nm-pill">Otoczenie</span>' +
                '</div>' +
                '<div class="nm-list-address">' + config.esc(otoczenieLabel) + '</div>' +
                (impactSummary ? '<div class="nm-card-subline">' + config.esc(impactSummary) + '</div>' : '') +
                '</button>' +
                '<div class="nm-list-meta-row">' +
                    '<span class="nm-chip ' + impactChipClass + '">' + config.esc(impactClassificationLabel || (otoczenieProps.negative ? 'Potencjalna uciążliwość' : 'Obiekt sąsiedni')) + renderInfoHelpButton(impactHelp.classification, 'Wyjaśnienie: klasyfikacja wpływu') + '</span>' +
                    (otoczenieProps.category_label ? '<span class="nm-chip"><b>Kategoria</b> ' + config.esc(otoczenieProps.category_label) + '</span>' : '') +
                    (otoczenieLayerLabel ? '<span class="nm-chip"><b>Warstwa</b> ' + config.esc(otoczenieLayerLabel) + '</span>' : '') +
                    (otoczenieProps.nuisance_level != null ? '<span class="nm-chip"><b>Uciążliwość</b> ' + config.esc(otoczenieProps.nuisance_level) + ' na 5' + renderInfoHelpButton(impactHelp.nuisance, 'Wyjaśnienie: uciążliwość') + '</span>' : '') +
                    (otoczenieProps.usefulness_level != null ? '<span class="nm-chip"><b>Użyteczność</b> ' + config.esc(otoczenieProps.usefulness_level) + ' na 5' + renderInfoHelpButton(impactHelp.usefulness, 'Wyjaśnienie: użyteczność') + '</span>' : '') +
                    (balanceLabel ? '<span class="nm-chip"><b>Bilans wpływu</b> ' + config.esc(balanceLabel) + renderInfoHelpButton(impactHelp.balance, 'Wyjaśnienie: bilans wpływu') + '</span>' : '') +
                '</div>' +
            '</article>';
        }


        if (hit.type === 'signal') {
            var signalProps = hit.properties || {};
            var signalLabel = signalProps.target_label || 'Sygnał społeczności';
            var signalSubtitle = signalProps.target_subtitle || '';
            var sentimentClass = signalProps.icon_sentiment === 'pro'
                ? 'nm-chip--success'
                : (signalProps.icon_sentiment === 'con' ? 'nm-chip--danger' : 'nm-chip--info');
            return '<button class="ms-card ms-card--grid nm-list-card nm-signal-card-preview' +
                (String(hit.id) === String(selectedIds.selectedSignalId) ? ' is-selected' : '') +
                '" type="button" data-tap-hit-kind="signal" data-tap-hit-id="' + config.esc(String(hit.id)) + '">' +
                '<div class="nm-list-card-top">' +
                    '<span class="nm-pill">' + config.esc((signalProps.icon || '💬') + ' Sygnał') + '</span>' +
                    '<span class="nm-chip ' + sentimentClass + '">' + config.esc(signalProps.category_name || 'Społeczność') + '</span>' +
                '</div>' +
                '<div class="nm-list-address">' + config.esc(signalLabel) + '</div>' +
                (signalSubtitle ? '<div class="nm-card-subline">' + config.esc(signalSubtitle) + '</div>' : '') +
                '<div class="nm-list-meta-row">' +
                    (signalProps.signal_count != null ? '<span class="nm-chip"><b>Sygnały</b> ' + config.esc(String(signalProps.signal_count)) + '</span>' : '') +
                    (signalProps.influence_radius_m != null ? '<span class="nm-chip"><b>Promień oddziaływania</b> ' + config.esc(formatDist(Number(signalProps.influence_radius_m))) + '</span>' : '') +
                    // A noun label: after „Dotyczy” the kind would need the genitive („działki”).
                    (signalProps.target_type ? '<span class="nm-chip"><b>Rodzaj celu</b> ' + config.esc(signalTargetLabel(signalProps.target_type)) + '</span>' : '') +
                '</div>' +
            '</button>';
        }

        if (hit.type === 'building') {
            var buildingProps = hit.properties || {};
            var buildingTypeLabel = buildingProps.rodzaj_label;
            return '<button class="ms-card ms-card--grid nm-list-card' +
                (String(hit.id) === String(selectedIds.selectedBid) ? ' is-selected' : '') +
                '" type="button" data-tap-hit-kind="building" data-tap-hit-id="' + config.esc(String(hit.id)) + '">' +
                '<div class="nm-list-card-top">' +
                    '<span class="nm-pill">Budynek</span>' +
                    // One flex item that may wrap between the segments of the identifier.
                    '<span class="nm-chip"><b>Identyfikator</b> <span>' + breakableHtml(String(hit.id)) + '</span></span>' +
                '</div>' +
                '<div class="nm-list-address">' + config.esc(buildingTypeLabel || 'Budynek z Ewidencji Gruntów i Budynków') + '</div>' +
                '<div class="nm-list-meta-row">' +
                    renderBuildingStoreysChip(buildingProps) +
                '</div>' +
            '</button>';
        }

        var parcelProps = hit.properties || {};
        var parcelAddress = [parcelProps.obreb, parcelProps.gmina].filter(Boolean).join(' · ');
        return '<article class="ms-card ms-card--grid nm-list-card nm-parcel-card' +
            (String(hit.id) === String(selectedIds.selectedDid) ? ' is-selected' : '') +
            '" data-tap-hit-kind="parcel" data-tap-hit-id="' + config.esc(String(hit.id)) + '">' +
                '<button class="nm-list-card-select nm-parcel-card__summary" type="button">' +
                    '<div class="nm-list-card-top">' +
                        '<span class="nm-pill">Działka</span>' +
                        (parcelProps.numer ? '<span class="nm-chip nm-parcel-card__number"><b>Numer</b> ' + config.esc(parcelProps.numer) + '</span>' : '') +
                    '</div>' +
                    '<div class="nm-list-address">' + config.esc(parcelAddress || ('Identyfikator działki ' + String(hit.id))) + '</div>' +
                    '<dl class="nm-parcel-card__facts">' +
                        '<div class="nm-parcel-card__fact"><dt>Identyfikator</dt><dd>' + config.esc(String(hit.id)) + '</dd></div>' +
                        (parcelProps.pole_ewid != null ? '<div class="nm-parcel-card__fact"><dt>Powierzchnia</dt><dd><strong>' + config.fmt(parcelProps.pole_ewid) + ' m²</strong></dd></div>' : '') +
                    '</dl>' +
                '</button>' +
                '<div class="nm-parcel-card__attributes">' + renderOwnershipChip(parcelProps) + renderLandUseChip(parcelProps) + '</div>' +
                '<button class="ms-button ms-button--primary ms-button--block nm-parcel-card__action" type="button" data-action="select-parcel">Wybierz działkę</button>' +
        '</article>';
    }

    function renderTapHitBrowsePanel() {
        var tapBrowseHits = state.getTapBrowseHits();
        config.listTitleEl.textContent = countLabel(tapBrowseHits.length, 'obiekt', 'obiekty', 'obiektów') + ' w tym miejscu';
        config.listMetaEl.textContent = 'Wybierz obiekt z listy, aby zobaczyć szczegóły lub opis';
        config.sortRowEl.style.display = 'none';
        config.listMoreBtn.style.display = 'none';

        var html = '<div class="nm-overlap-back-row">' +
            '<button class="ms-button ms-button--secondary ms-button--sm nm-overlap-back-btn" id="nm-tap-browse-back" type="button">← Wróć do listy</button>' +
        '</div>';
        html += '<div class="nm-list-stack">';
        tapBrowseHits.forEach(function(hit) {
            html += renderTapHitCardHtml(hit);
        });
        html += '</div>';

        config.listContentEl.innerHTML = html;
        config.listContentEl.__listContentHtml = null;
    }

    function resolveTapHitSelection(kind, id, latlng) {
        var tapBrowseHits = state.getTapBrowseHits();
        var candidateHits = tapBrowseHits && tapBrowseHits.length
            ? tapBrowseHits
            : (state.getCombinedBrowseOtherHits ? state.getCombinedBrowseOtherHits() : null);

        if (!candidateHits || !candidateHits.length) {
            return;
        }

        var stringId = String(id);
        var hit = candidateHits.find(function(entry) {
            return entry.type === kind && String(entry.id) === stringId;
        });
        if (!hit) {
            return;
        }

        clearTapBrowseState();

        if (hit.type === 'gunb') {
            config.selectGunbPermit(hit.feature, latlng);
            return;
        }

        if (hit.type === 'otoczenie') {
            if (config.selectOtoczenieFeature) {
                config.selectOtoczenieFeature(hit.feature, latlng || config.getFeatureCentroid(hit.feature));
            }
            return;
        }

        if (hit.type === 'signal') {
            if (config.selectSignalHit) {
                config.selectSignalHit(hit.properties, latlng || config.getFeatureCentroid(hit.feature));
            }
            return;
        }

        if (hit.type === 'building') {
            config.selectBuilding(hit.properties, latlng);
            return;
        }

        if (hit.type === 'education') {
            if (config.selectEducationHit) {
                config.selectEducationHit(hit, config.getFeatureCentroid(hit.feature) || latlng);
            }
            return;
        }

        if (hit.type === 'noise') {
            config.selectNoiseHit(hit, latlng || config.getFeatureCentroid(hit.feature));
            return;
        }

        config.selectParcel(hit.properties, latlng);
    }

    function getTapBrowseLatlng() {
        return state.getTapBrowseLatlng();
    }

    function hasTapBrowseHits() {
        var tapBrowseHits = state.getTapBrowseHits();
        return !!(tapBrowseHits && tapBrowseHits.length);
    }

    return {
        browseTapHitsInPanel: browseTapHitsInPanel,
        clearTapBrowseState: clearTapBrowseState,
        exitCombinedBrowse: exitCombinedBrowse,
        exitTapBrowse: exitTapBrowse,
        getTapBrowseLatlng: getTapBrowseLatlng,
        hasTapBrowseHits: hasTapBrowseHits,
        renderTapHitCardHtml: renderTapHitCardHtml,
        renderTapHitBrowsePanel: renderTapHitBrowsePanel,
        resolveTapHitSelection: resolveTapHitSelection,
    };
}
